import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import { Container, Stack, Flex, Grid } from '../../design-system/components/Layout';
import { Heading, Text, Label } from '../../design-system/components/Typography';
import { EmptyState } from '../../components/common/EmptyState';
import { Badge } from '../../design-system/components/Decorative';
import { PageHeader } from '../../design-system/patterns/PageHeader';
import { StatusIndicator } from '../../design-system/patterns/StatusIndicator';
import SearchBar from '../../components/common/SearchBar';
import { GroupedList } from '../../components/common/GroupedList';
import { GroupingSelector, GroupingOption } from '../../components/common/GroupingSelector';
import { groupByStatus, groupByDate, useGroupLabels } from '../../utils/grouping';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';
import { EmailCampaign, EmailCampaignStatus } from '../../types/email';
import { adminService } from '../../services/admin';
import { PagedEmailCampaigns } from '../../types/email';
import { hexToRgba } from '../../utils/colorUtils';
import { enumMatches, tryNormalizeEnumValue } from '../../utils/enumNormalize';

const HistoryContainer = styled(Container)`
  max-width: 1400px;
`;

const CampaignsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const CampaignCard = styled(Card)`
  padding: ${spacing[6]};
  position: relative;
`;

const CampaignHeader = styled(Flex)`
  margin-bottom: ${spacing[6]};
  align-items: flex-start;
`;

const CampaignTitle = styled(Heading)`
  margin: 0;
  flex: 1;
`;

const CampaignInfo = styled(Flex)`
  flex-wrap: wrap;
  gap: ${spacing[6]};
  margin-bottom: ${spacing[6]};
`;

const InfoItem = styled(Stack)`
  min-width: 150px;
`;

const InfoLabel = styled(Text).attrs(() => ({
  size: 'xs' as const,
}))`
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: ${typography.letterSpacing.wide};
  font-weight: ${typography.fontWeight.semibold};
`;

const InfoValue = styled(Text).attrs(() => ({
  size: 'base' as const,
}))`
  color: ${props => props.theme.colors.text};
  font-weight: ${typography.fontWeight.semibold};
`;

const CampaignSubject = styled(Card)`
  padding: ${spacing[4]};
  margin-bottom: ${spacing[6]};
  background: ${props => props.theme.colors.surfaceHover};
  border-left: 4px solid ${props => props.theme.colors.primary};
`;

const EmptyStateCard = styled(Card)`
  text-align: center;
  padding: ${spacing[16]};
  background: ${props => props.theme.colors.surfaceHover};
`;

const Pagination = styled(Flex)`
  justify-content: center;
  align-items: center;
  margin-top: ${spacing[8]};
`;

const CardWithPadding = styled(Card)`
  padding: ${spacing[8]};
`;

const FlexWrap = styled(Flex)`
  flex-wrap: wrap;
`;

const SearchContainer = styled.div`
  flex: 1;
  min-width: 200px;
`;

const ButtonWithMargin = styled(Button)`
  margin-top: ${spacing[4]};
`;

const ErrorCard = styled(Card)`
  padding: ${spacing[4]};
  background: ${props => hexToRgba(props.theme.colors.error, 0.1)};
  border-left: 4px solid ${props => props.theme.colors.error};
  margin-top: ${spacing[4]};
`;

const EmailCampaignHistory: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [grouping, setGrouping] = useState<string>('none');
  const [statusFilter, setStatusFilter] = useState<EmailCampaignStatus | 'all'>('all');
  const { getStatusLabel: getGroupStatusLabel, getDateLabel } = useGroupLabels();
  const groupingOptions: GroupingOption[] = [
    { value: 'none', label: t('grouping.none') },
    { value: 'status', label: t('grouping.statusLabel') },
    { value: 'date', label: t('grouping.dateLabel') },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadCampaigns();
  }, [page]);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const result: PagedEmailCampaigns = await adminService.getEmailCampaigns(page, 10);
      setCampaigns(result.items);
      setTotalPages(result.totalPages);
      setTotal(result.total);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusType = (
    status?: EmailCampaignStatus | string | number | null
  ): 'success' | 'warning' | 'error' | 'info' | 'pending' | 'completed' => {
    const normalized = tryNormalizeEnumValue(EmailCampaignStatus, status);
    switch (normalized) {
      case EmailCampaignStatus.Completed:
        return 'completed';
      case EmailCampaignStatus.Sending:
        return 'warning';
      case EmailCampaignStatus.Failed:
        return 'error';
      case EmailCampaignStatus.Draft:
        return 'pending';
      case EmailCampaignStatus.Cancelled:
        return 'info';
      default:
        return 'info';
    }
  };

  const getStatusLabel = (status?: EmailCampaignStatus | string | number | null): string => {
    const normalized = tryNormalizeEnumValue(EmailCampaignStatus, status);
    if (normalized == null) return '—';
    const labels: Record<EmailCampaignStatus, string> = {
      [EmailCampaignStatus.Draft]: t('email.statusDraft'),
      [EmailCampaignStatus.Sending]: t('email.statusSending'),
      [EmailCampaignStatus.Completed]: t('email.statusCompleted'),
      [EmailCampaignStatus.Failed]: t('email.statusFailed'),
      [EmailCampaignStatus.Cancelled]: t('email.statusCancelled'),
    };
    return labels[normalized] || '—';
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const filteredCampaigns = campaigns.filter((campaign) => {
    if (statusFilter !== 'all' && !enumMatches(EmailCampaignStatus, campaign.status, statusFilter)) {
      return false;
    }
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.trim().toLowerCase();
      const haystack = `${campaign.name ?? ''} ${campaign.subject ?? ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <HistoryContainer>
        <PageHeader
          title={t('email.campaignHistory')}
          pattern={true}
        />
        <Text>{t('common.loading')}</Text>
      </HistoryContainer>
    );
  }

  return (
    <HistoryContainer>
      <Stack direction="column" spacing="xl">
        <PageHeader
          title={t('email.campaignHistory')}
          subtitle={`${total} ${t('email.totalCampaigns')}`}
          actions={
            <Button variant="primary" onClick={() => navigate('/admin/email')} glow ripple>
              {t('email.newCampaign')}
            </Button>
          }
          pattern={true}
        />
        
        <CardWithPadding variant="elevated" headerVariant="gradient" accentBorder="top" pattern="dots" patternIntensity="subtle">
          <Stack direction="column" spacing="lg">
            <Heading variant="h3" color="text">
              {t('email.filters')}
            </Heading>
            <FlexWrap direction="row" gap="md" align="center">
              <SearchContainer>
                <SearchBar
                  value={search}
                  onChange={setSearch}
                  onSearch={setDebouncedSearch}
                  placeholder={t('email.searchPlaceholder')}
                  loading={loading}
                  fullWidth
                />
              </SearchContainer>
              <select
                value={statusFilter === 'all' ? 'all' : statusFilter.toString()}
                onChange={(e) => setStatusFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value) as EmailCampaignStatus)}
                style={{
                  padding: spacing[3],
                  border: `2px solid ${theme.colors.border}`,
                  borderRadius: borders.radius.md,
                  fontSize: typography.fontSize.sm,
                  background: theme.colors.surface,
                  color: theme.colors.text,
                }}
              >
                <option value="all">{t('email.allStatuses')}</option>
                <option value={EmailCampaignStatus.Draft.toString()}>{t('email.statusDraft')}</option>
                <option value={EmailCampaignStatus.Sending.toString()}>{t('email.statusSending')}</option>
                <option value={EmailCampaignStatus.Completed.toString()}>{t('email.statusCompleted')}</option>
                <option value={EmailCampaignStatus.Failed.toString()}>{t('email.statusFailed')}</option>
                <option value={EmailCampaignStatus.Cancelled.toString()}>{t('email.statusCancelled')}</option>
              </select>
              <GroupingSelector
                value={grouping}
                onChange={setGrouping}
                options={groupingOptions}
                label={t('grouping.groupBy')}
              />
            </FlexWrap>
          </Stack>
        </CardWithPadding>

      {filteredCampaigns.length === 0 ? (
        <EmptyStateCard>
          <Stack direction="column" spacing="lg" align="center">
            <Heading variant="h3" color="text">
              {t('email.noCampaigns')}
            </Heading>
            <Text color="secondary" size="lg">
              {t('email.createFirstCampaign')}
            </Text>
            <ButtonWithMargin 
              variant="primary" 
              onClick={() => navigate('/admin/email')} 
              glow
              ripple
            >
              {t('email.newCampaign')}
            </ButtonWithMargin>
          </Stack>
        </EmptyStateCard>
      ) : (
        <>
          <Stack direction="column" spacing="lg">
            {filteredCampaigns.map((campaign) => (
              <CampaignCard
                key={campaign.id}
                variant="elevated"
                accentBorder="top"
              >
                <CampaignHeader direction="row" justify="space-between" align="start">
                  <CampaignTitle variant="h3" color="text">
                    {campaign.name}
                  </CampaignTitle>
                  <StatusIndicator
                    status={getStatusType(campaign.status)}
                    label={getStatusLabel(campaign.status)}
                    size="md"
                  />
                </CampaignHeader>

                <CampaignSubject variant="filled">
                  <Text size="sm" weight="medium">
                    <strong>{t('email.subject')}:</strong> {campaign.subject}
                  </Text>
                </CampaignSubject>

                <CampaignInfo direction="row" wrap="wrap">
                  <InfoItem direction="column" spacing="sm">
                    <InfoLabel>{t('email.recipients')}</InfoLabel>
                    <Text size="lg" weight="bold">{campaign.recipientCount}</Text>
                  </InfoItem>
                  <InfoItem direction="column" spacing="sm">
                    <InfoLabel>{t('email.sent')}</InfoLabel>
                    <Text size="lg" color="success" weight="bold">{campaign.sentCount}</Text>
                  </InfoItem>
                  <InfoItem direction="column" spacing="sm">
                    <InfoLabel>{t('email.failed')}</InfoLabel>
                    <Text size="lg" color={campaign.failedCount > 0 ? 'error' : 'text'} weight="bold">
                      {campaign.failedCount}
                    </Text>
                  </InfoItem>
                  <InfoItem direction="column" spacing="sm">
                    <InfoLabel>{t('email.created')}</InfoLabel>
                    <InfoValue>{formatDate(campaign.createdAt)}</InfoValue>
                  </InfoItem>
                  {campaign.sentAt && (
                    <InfoItem direction="column" spacing="sm">
                      <InfoLabel>{t('email.sentAt')}</InfoLabel>
                      <InfoValue>{formatDate(campaign.sentAt)}</InfoValue>
                    </InfoItem>
                  )}
                  {campaign.createdByName && (
                    <InfoItem direction="column" spacing="sm">
                      <InfoLabel>{t('email.createdBy')}</InfoLabel>
                      <InfoValue>{campaign.createdByName}</InfoValue>
                    </InfoItem>
                  )}
                </CampaignInfo>

                {campaign.errorMessage && (
                  <ErrorCard variant="filled">
                    <Text size="sm" color="error" weight="medium">
                      <strong>{t('email.error')}:</strong> {campaign.errorMessage}
                    </Text>
                  </ErrorCard>
                )}
              </CampaignCard>
            ))}
          </Stack>

          {totalPages > 1 && (
            <Pagination direction="row" gap="lg" align="center">
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                glow
              >
                {t('email.previous')}
              </Button>
              <Text color="secondary">
                {t('email.page')} {page} {t('email.of')} {totalPages}
              </Text>
              <Button
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                glow
              >
                {t('email.next')}
              </Button>
            </Pagination>
          )}
        </>
      )}
      </Stack>
    </HistoryContainer>
  );
};

export default EmailCampaignHistory;
